/**
 * Usage Configuration for Products
 * Uses total_base_units for lot selection.
 * Inserts product_inventory_transactions with type 'usage'.
 * Deducts pallet/case/unit quantities from source lot and recalculates aggregates.
 */

import { supabase } from '../../../../../supabaseClient';

export const usageProductConfig = {
  type:              'products',
  displayName:       'Products',
  singularName:      'Product',
  itemIdField:       'product_id',
  itemNameField:     'name',

  inventoryTable:    'product_inventory',
  itemsTable:        'product_inventory_items',
  transactionsTable: 'product_inventory_transactions',

  /**
   * Get available lot items for a product at a location.
   * Returns current_quantity alias (= total_base_units) for stepper compatibility.
   * Also returns variant_name from product_variants join.
   */
  getInventoryItems: async (companyId, itemId, locationId) => {
    const { data, error } = await supabase
      .from('product_inventory_items')
      .select(`
        *,
        products (
          id, name, sku
        ),
        product_variants (id, name)
      `)
      .eq('company_id', companyId)
      .eq('product_id', itemId)
      .eq('location_id', locationId)
      .eq('is_active', true)
      .gt('total_base_units', 0)
      .order('created_at', { ascending: true });

    if (error) throw error;

    return (data || []).map(item => ({
      ...item,
      current_quantity: item.total_base_units,
      variant_name: item.product_variants?.name || null
    }));
  },

  /**
   * Execute usage transactions for selected lots.
   * Deducts pallet/case/unit quantities from source lot and recalculates aggregates.
   */
  executeUsage: async (companyId, usageData) => {
    const { locationId, usages, notes } = usageData;

    for (const usage of usages) {
      const { itemId, lotId, pallets = 0, cases = 0, units = 0, lot_number, batch_number } = usage;

      // Fetch lot to get variant_id and unit config
      const { data: lotData } = await supabase
        .from('product_inventory_items')
        .select('*')
        .eq('id', lotId)
        .single();

      const variantId      = lotData?.variant_id || null;
      const unitsPerCase   = lotData?.units_per_case   || 1;
      const casesPerPallet = lotData?.cases_per_pallet || 1;

      // Total base units being deducted (capped at what the lot actually has)
      const requestedBase = (pallets * casesPerPallet * unitsPerCase)
                          + (cases   * unitsPerCase)
                          + units;
      const totalBaseUnits = Math.min(requestedBase, lotData?.total_base_units || 0);

      // Insert usage transaction (negative quantity_change in base units)
      const { error: txError } = await supabase
        .from('product_inventory_transactions')
        .insert([{
          company_id:       companyId,
          product_id:       itemId,
          variant_id:       variantId,
          transaction_type: 'usage',
          quantity_change:  -Math.abs(totalBaseUnits),
          from_location_id: locationId,
          input_pallets:    pallets,
          input_cases:      cases,
          input_units:      units,
          lot_number:       lot_number,
          batch_number:     batch_number,
          units_per_case:   unitsPerCase,
          cases_per_pallet: casesPerPallet,
          created_at:       new Date().toISOString(),
          notes:            notes || null
        }]);

      if (txError) throw txError;

      // Re-pack remaining base units back into tiers.
      // This handles breakdown: e.g. taking 5 cases from a 1-pallet lot
      // results in 0 pallets + (cpp - 5) cases, not a negative case count.
      let leftover = (lotData?.total_base_units || 0) - totalBaseUnits;
      let newPallets = 0, newCases = 0, newUnits = 0;

      if (lotData?.enable_pallets && casesPerPallet > 0) {
        newPallets = Math.floor(leftover / (casesPerPallet * unitsPerCase));
        leftover  -= newPallets * casesPerPallet * unitsPerCase;
      }
      if (lotData?.enable_cases && unitsPerCase > 0) {
        newCases  = Math.floor(leftover / unitsPerCase);
        leftover -= newCases * unitsPerCase;
      }
      newUnits = leftover;

      await supabase
        .from('product_inventory_items')
        .update({
          pallet_quantity:  newPallets,
          case_quantity:    newCases,
          unit_quantity:    newUnits,
          is_active:        newPallets > 0 || newCases > 0 || newUnits > 0,
          last_updated_at:  new Date().toISOString()
        })
        .eq('id', lotId);

      // Recalculate aggregate inventory record
      await supabase.rpc('recalculate_product_inventory_aggregate', {
        p_company_id:  companyId,
        p_product_id:  itemId,
        p_variant_id:  variantId,
        p_location_id: locationId
      });
    }
  }
};
